import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  addMembersToCloudIdentityGroup,
  getCloudIdentityGroupMembers,
  getDirectoryGroupQuery,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { cloudidentity_v1 } from '@googleapis/cloudidentity';
import { handleCIGroupError } from './bulk-add-group-ci-members-dialog';
import SharedDialog from '../../../../shared/shared-dialog';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface CloneGroupCIMembersDialogProps {
  open: boolean;
  handleClose: () => void;
  groupName: string;
  groupEmail: string;
  groupId: string;
  changeLogRoles: string[];
  customerId: string;
  hasSecurityLabelsOrSettings: boolean;
}

export default function CloneGroupCIMembersDialog({
  open,
  handleClose,
  groupName,
  groupEmail,
  changeLogRoles,
  groupId,
  customerId,
  hasSecurityLabelsOrSettings,
}: CloneGroupCIMembersDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [autoCompleteValue, setAutoCompleteValue] = useState('');
  const [gettingMembersLoading, setGettingMembersLoading] = useState(false);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: (
      memberships: cloudidentity_v1.Schema$Membership[] | undefined
    ) => {
      const membersToAdd = (memberships || [])
        .filter(
          (member) =>
            !hasSecurityLabelsOrSettings ||
            member.preferredMemberKey?.id !== customerId
        )
        .map(
          ({
            name: _name,
            createTime: _createTime,
            updateTime: _updateTime,
            type: _type,
            roles,
            ...rest
          }) => ({
            ...rest,
            roles: roles?.map(
              ({ restrictionEvaluations: _restrictionEvaluations, ...role }) =>
                role
            ),
            preferredMemberKey: {
              id:
                rest.preferredMemberKey?.id === customerId
                  ? '*'
                  : rest.preferredMemberKey?.id,
            },
          })
        );

      return addMembersToCloudIdentityGroup(
        membersToAdd,
        groupId,
        groupEmail,
        changeLogMessage
      );
    },
    onSuccess: (_returnData, variables) => {
      const membersCount = variables?.length ?? 0;
      setSnackbar({
        open: true,
        severity: membersCount > 1 ? 'info' : 'success',
        message:
          membersCount > 1
            ? 'The task to add the group members from the selected group has begun.'
            : 'Group member added successfully!',
      });

      if (membersCount > 0) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.GroupMembersCloudIdentity, groupEmail],
        });
        queryClient.invalidateQueries({
          queryKey: [UserKeys.DirectoryGroup, groupEmail],
        });
      }
      setAutoCompleteValue('');
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleCIGroupError(error, groupEmail);
    },
    onSettled: () => {
      createEvent(analytics.directory.group_details.clone_group_members, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.groups,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const getSourceMembersAndMutate = async () => {
    setGettingMembersLoading(true);
    try {
      const sourceGroupQueryOptions = getDirectoryGroupQuery(autoCompleteValue);
      const sourceGroup = await queryClient.fetchQuery(sourceGroupQueryOptions);

      if (sourceGroup?.id) {
        const sourceMembersQueryOptions = getCloudIdentityGroupMembers(
          autoCompleteValue,
          sourceGroup.id
        );
        const response = await queryClient.fetchQuery(
          sourceMembersQueryOptions
        );
        mutate(response);
      } else {
        throw new Error(`Group ${autoCompleteValue} not found or has no ID.`);
      }
    } catch (e) {
      handleErrors({
        error: e,
        snackbarMessage: `Could not find group ${autoCompleteValue}. Please ensure it is a Cloud Identity group.`,
      });
    } finally {
      setGettingMembersLoading(false);
    }
  };

  async function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.AddMemberToGroup.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      await getSourceMembersAndMutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        setAutoCompleteValue('');
        handleClose();
      }}
      title="Add from Group"
      dialogContent={
        <Stack gap={3}>
          <Typography
            sx={{
              mt: 2,
            }}
          >
            Add members to <strong>{groupName}</strong> from selected group.
          </Typography>
          <Box sx={{ width: '100%' }}>
            <SharedAutocomplete
              sources={['groups']}
              autocompleteValue={autoCompleteValue}
              setAutoCompleteValue={setAutoCompleteValue}
            />
          </Box>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={getSourceMembersAndMutate}
            mutateLoading={isPending || gettingMembersLoading}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Add Members',
        action: handleSubmit,
        loading: isPending || gettingMembersLoading,
        disabled:
          isPending || autoCompleteValue.length === 0 || gettingMembersLoading,
      }}
    />
  );
}
